import { useMemo, useState } from 'react';
import { Menu } from '@headlessui/react';
import { ChevronDownIcon as ArrowIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import { useHits } from 'react-instantsearch';
import { useFormat } from 'helpers/hooks/useFormat';
import { useLocalizedIndex } from 'providers/algolia/localized-index';
import useDynamicFacets from '../../hooks/useDynamicFacets';
import styles from '../../styles/index.module.css';
import { FacetConfiguration } from '../../types';
import SortFacet from '../facets/sort';
import { useSortBy } from 'react-instantsearch-core';
import Breadcrumb from 'components/commercetools-ui/atoms/breadcrumb';
import { usePathname } from 'next/navigation';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import Link from 'components/commercetools-ui/atoms/link';
import { Category } from 'shared/types/product/Category';
import SortBy from '../sort-by';
import BreadCrumbData from 'public/locales/en/product.json';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
interface Props {
  facetsConfiguration: Record<string, FacetConfiguration>;
  breadCrumbs?: BreadCrumb[];
  searchQuery?: string;
  currentCategory?: Category;
  // BrandAndSubCategory: string | undefined;
  isNotFound?: boolean;
  nameOfCategory: string;
}

const DesktopFacetsUpdate: React.FC<Props> = ({
  facetsConfiguration,
  breadCrumbs,
  searchQuery,
  currentCategory,
  isNotFound,
  nameOfCategory,
}) => {
  const { indexName: productsIndex } = useLocalizedIndex({ type: 'products' });
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const { hits } = useHits();
  const pathname = usePathname();

  const { refine, currentRefinement } = useSortBy({
    items: [
      { label: 'A-Z', value: `${productsIndex}_A-Z` },
      { label: 'Z-A', value: `${productsIndex}_Z-A` },
      { label: 'Low to High', value: `${productsIndex}_price_asc` },
      { label: 'High to Low', value: `${productsIndex}_price_desc` },
    ],
  });

  // Local state to manage selected sort option
  const [selectedSort, setSelectedSort] = useState('Please select one');
  // Update state & Algolia sort
  const handleSortChange = (value: string) => {
    setSelectedSort(value);
    refine(value);
  };

  // Filter out "BRANDS" from the breadcrumbs if it's not the last one
  const filteredBreadCrumbs = breadCrumbs
    ?.map((breadCrumb, index) => {
      // If the breadcrumb is "BRANDS" and it's not the last breadcrumb, remove it
      if (breadCrumb.name === 'BRANDS' && breadCrumbs.length > index + 1) {
        return null; // return null to remove it
      }
      return breadCrumb;
    })
    .filter(Boolean); // Filter out null values

  return (
    <div className="hidden items-center justify-between pb-16 lg:flex">
      <div>
        {searchQuery ? (
          <div>
            <div className="flex items-center pl-15">
              <SmartLink href={'/'} className="text-14">
                {BreadCrumbData?.BreadCrumb}
              </SmartLink>
              <ChevronRightIcon
                className="mt-2 h-15 w-18 font-bold text-[#127e57]"
                stroke="currentColor"
                strokeWidth="3"
                fill="none"
              />
              <p className="text-14 capitalize">
                <span> {`${searchQuery?.toLowerCase()}`}</span>
              </p>
            </div>
          </div>
        ) : (
          <div>
            {(filteredBreadCrumbs?.length as any) > 0 && !searchQuery && (
              <Breadcrumb
                useSeparatorNode
                Separator={<div className="relative top-[2px] text-[#127e57]">{'>'}</div>}
                className="col-span-12 w-fit"
                dynamicPadding={true}
                style={{ paddingLeft: '0px' }}
              >
                {filteredBreadCrumbs?.map((breadCrumb) => {
                  const isActive = breadCrumb && pathname === breadCrumb?.href;
                  return (
                    <Link
                      key={breadCrumb?.href}
                      link={breadCrumb?.href}
                      className={`text-14 font-normal ${
                        isActive ? 'font-semibold text-primary-black' : 'text-secondary-grey'
                      }`}
                    >
                      {breadCrumb?.name}
                    </Link>
                  );
                })}
              </Breadcrumb>
            )}
          </div>
        )}
      </div>
      {
        <>
          {/* <p className="font-body text-22 font-semibold leading-[40px] lg:text-36">{currentCategory?.name}</p> */}
          {searchQuery ? (
            <h1 className="mt-5 flex-1 text-center text-36 font-semibold leading-[40px]">
              Search results - {searchQuery?.toUpperCase()}
            </h1>
          ) : (
            <h1 className="font-body text-22 font-semibold leading-[40px] lg:text-36">{nameOfCategory}</h1>
          )}

          <div className="flex items-center gap-18">
            {isNotFound && <SortBy isSearch={searchQuery ? true : false} />}
          </div>
        </>
      }
    </div>
  );
};

export default DesktopFacetsUpdate;
